// web/src/components/enroll/SecretGenerator.tsx — the in-wizard secret generator
// (REDESIGN-PLAN M4.6). Lives inside the New-secret wizard's Material step. Two
// modes: "Generate" (xkpasswd-style, client-side only) and "Provide your own"
// (the existing textarea + reveal). Whichever mode is active feeds its value up
// via onMaterialChange — the wizard stores it in the same `material` draft field
// the seal flow reads, so the downstream client-side seal encrypts it unchanged.
//
// Randomness invariant: every random choice uses crypto.getRandomValues. Never
// Math.random; the generated value is never logged. Entropy is computed from the
// real configuration (word-list size, charset^length, 10^digits), not guessed.
import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '../ui/Icon';
import { Check, Copy, RefreshCw } from 'lucide-react';
import { WORDLIST, BITS_PER_WORD } from './wordlist';

export interface SecretGeneratorProps {
  /** Current material value (the wizard's draft field). */
  material: string;
  /** Push a new material value up to the wizard draft. */
  onMaterialChange: (value: string) => void;
}

type Mode = 'generate' | 'provide';
type Preset = 'passphrase' | 'random' | 'pin';

const SEPARATORS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'hyphen -', value: '-' },
  { label: 'period .', value: '.' },
  { label: 'space', value: ' ' },
  { label: 'none', value: '' },
];

const SYMBOL_PAD = '!@#$%^&*';
const LOWER = 'abcdefghijkmnpqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGITS = '23456789';
const SYMBOLS = '!@#$%^&*-_=+';

interface PassphraseCfg {
  words: number;
  separator: string;
  capitalize: boolean;
  appendDigits: number;
  symbolPadding: number;
}
interface RandomCfg {
  length: number;
  lower: boolean;
  upper: boolean;
  digits: boolean;
  symbols: boolean;
}
interface PinCfg {
  length: number;
}

const DEFAULT_PASSPHRASE: PassphraseCfg = {
  words: 4,
  separator: '-',
  capitalize: true,
  appendDigits: 2,
  symbolPadding: 0,
};
const DEFAULT_RANDOM: RandomCfg = {
  length: 20,
  lower: true,
  upper: true,
  digits: true,
  symbols: true,
};
const DEFAULT_PIN: PinCfg = { length: 6 };

/** Uniform integer in [0, max) via rejection sampling over crypto bytes.
 * Samples 32 bits so any `max` up to 2^32 is supported (a single byte breaks
 * for max > 256: the fair `limit` floors to 0 and the loop never exits). */
function randomInt(max: number): number {
  if (max <= 0) return 0;
  // Reject values in the unfair tail so every outcome stays equiprobable.
  const limit = Math.floor(0x1_0000_0000 / max) * max;
  const buf = new Uint32Array(1);
  let n: number;
  do {
    crypto.getRandomValues(buf);
    n = buf[0]!;
  } while (n >= limit);
  return n % max;
}

/** Pick one element of a string/array uniformly at random. */
function pick<T>(items: ArrayLike<T>): T {
  return items[randomInt(items.length)]!;
}

function generatePassphrase(cfg: PassphraseCfg): string {
  const parts: string[] = [];
  for (let i = 0; i < cfg.words; i++) {
    let word = pick(WORDLIST);
    if (cfg.capitalize) word = word.charAt(0).toUpperCase() + word.slice(1);
    parts.push(word);
  }
  let out = parts.join(cfg.separator);
  if (cfg.appendDigits > 0) {
    let tail = '';
    for (let i = 0; i < cfg.appendDigits; i++) tail += String(randomInt(10));
    out += cfg.separator + tail;
  }
  if (cfg.symbolPadding > 0) {
    let pad = '';
    for (let i = 0; i < cfg.symbolPadding; i++) pad += pick(SYMBOL_PAD);
    out = pad + out + pad;
  }
  return out;
}

function randomCharset(cfg: RandomCfg): string {
  let charset = '';
  if (cfg.lower) charset += LOWER;
  if (cfg.upper) charset += UPPER;
  if (cfg.digits) charset += DIGITS;
  if (cfg.symbols) charset += SYMBOLS;
  return charset;
}

function generateRandom(cfg: RandomCfg): string {
  const charset = randomCharset(cfg);
  if (charset.length === 0) return '';
  let out = '';
  for (let i = 0; i < cfg.length; i++) out += pick(charset);
  return out;
}

function generatePin(cfg: PinCfg): string {
  let out = '';
  for (let i = 0; i < cfg.length; i++) out += String(randomInt(10));
  return out;
}

/** Honest entropy in bits, computed from the active config (not the string). */
function entropyBits(preset: Preset, p: PassphraseCfg, r: RandomCfg, n: PinCfg): number {
  if (preset === 'passphrase') {
    // Each word: log2(list size). Capitalisation here is fixed (all-or-none), so
    // it adds no per-word choice and contributes no entropy. Appended digits:
    // log2(10) each. Symbol padding: the same symbol set drawn per position, and
    // it is mirrored front/back, so only `symbolPadding` independent draws.
    let bits = p.words * BITS_PER_WORD;
    bits += p.appendDigits * Math.log2(10);
    bits += p.symbolPadding * Math.log2(SYMBOL_PAD.length);
    return bits;
  }
  if (preset === 'random') {
    const size = randomCharset(r).length;
    return size > 0 ? r.length * Math.log2(size) : 0;
  }
  return n.length * Math.log2(10);
}

type Strength = { label: string; tone: 'danger' | 'warning' | 'success'; pct: number };

/** Map bits to a calm Weak/Fair/Strong band. 60 bits = fair, 90+ = strong. */
function strengthOf(bits: number): Strength {
  const pct = Math.max(0, Math.min(100, Math.round((bits / 120) * 100)));
  if (bits < 60) return { label: 'Weak', tone: 'danger', pct };
  if (bits < 90) return { label: 'Fair', tone: 'warning', pct };
  return { label: 'Strong', tone: 'success', pct };
}

export function SecretGenerator({ material, onMaterialChange }: SecretGeneratorProps): React.JSX.Element {
  const [mode, setMode] = useState<Mode>('generate');
  const [preset, setPreset] = useState<Preset>('passphrase');
  const [pp, setPp] = useState<PassphraseCfg>(DEFAULT_PASSPHRASE);
  const [rnd, setRnd] = useState<RandomCfg>(DEFAULT_RANDOM);
  const [pin, setPin] = useState<PinCfg>(DEFAULT_PIN);

  const [showProvide, setShowProvide] = useState(false);
  const [copied, setCopied] = useState(false);

  const regenerate = useCallback(() => {
    let value = '';
    if (preset === 'passphrase') value = generatePassphrase(pp);
    else if (preset === 'random') value = generateRandom(rnd);
    else value = generatePin(pin);
    onMaterialChange(value);
  }, [preset, pp, rnd, pin, onMaterialChange]);

  // Re-roll whenever the active generator config changes (and on first mount in
  // generate mode), so the preview always matches the controls.
  useEffect(() => {
    if (mode === 'generate') regenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, preset, pp, rnd, pin]);

  function copy(): void {
    navigator.clipboard?.writeText(material).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => undefined,
    );
  }

  const bits = entropyBits(preset, pp, rnd, pin);
  const strength = strengthOf(bits);
  const noCharset = preset === 'random' && randomCharset(rnd).length === 0;

  return (
    <div style={{ display: 'grid', gap: '0.95rem' }}>
      <div className="bgGenModes" role="group" aria-label="Material source">
        <button
          type="button"
          className={`bgGenModeBtn ${mode === 'generate' ? 'bgGenModeActive' : ''}`}
          aria-pressed={mode === 'generate'}
          onClick={() => setMode('generate')}
        >
          Generate
        </button>
        <button
          type="button"
          className={`bgGenModeBtn ${mode === 'provide' ? 'bgGenModeActive' : ''}`}
          aria-pressed={mode === 'provide'}
          onClick={() => setMode('provide')}
        >
          Provide your own
        </button>
      </div>

      {mode === 'generate' && (
        <>
          <div className="bgChips" role="group" aria-label="Generator style">
            {([
              ['passphrase', 'Memorable passphrase'],
              ['random', 'Strong random'],
              ['pin', 'PIN'],
            ] as ReadonlyArray<[Preset, string]>).map(([key, label]) => {
              const active = preset === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`bgChip ${active ? 'bgChipActive' : ''}`}
                  aria-pressed={active}
                  onClick={() => setPreset(key)}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {preset === 'passphrase' && (
            <div className="bgGenTunables">
              <label className="bgGenTunable">
                <span className="field-label">Words: {pp.words}</span>
                <input
                  type="range"
                  min={3}
                  max={8}
                  value={pp.words}
                  onChange={(e) => setPp({ ...pp, words: Number(e.target.value) })}
                />
              </label>
              <label className="bgGenTunable">
                <span className="field-label">Separator</span>
                <select
                  className="input"
                  value={pp.separator}
                  onChange={(e) => setPp({ ...pp, separator: e.target.value })}
                >
                  {SEPARATORS.map((s) => (
                    <option key={s.label} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </label>
              <label className="bgGenTunable">
                <span className="field-label">Append digits: {pp.appendDigits}</span>
                <input
                  type="range"
                  min={0}
                  max={4}
                  value={pp.appendDigits}
                  onChange={(e) => setPp({ ...pp, appendDigits: Number(e.target.value) })}
                />
              </label>
              <label className="bgGenTunable">
                <span className="field-label">Symbol padding: {pp.symbolPadding}</span>
                <input
                  type="range"
                  min={0}
                  max={4}
                  value={pp.symbolPadding}
                  onChange={(e) => setPp({ ...pp, symbolPadding: Number(e.target.value) })}
                />
              </label>
              <label className="bgGenCheck">
                <input
                  type="checkbox"
                  checked={pp.capitalize}
                  onChange={(e) => setPp({ ...pp, capitalize: e.target.checked })}
                />
                <span>Capitalize each word</span>
              </label>
            </div>
          )}

          {preset === 'random' && (
            <div className="bgGenTunables">
              <label className="bgGenTunable">
                <span className="field-label">Length: {rnd.length}</span>
                <input
                  type="range"
                  min={8}
                  max={64}
                  value={rnd.length}
                  onChange={(e) => setRnd({ ...rnd, length: Number(e.target.value) })}
                />
              </label>
              <div className="bgGenClasses" role="group" aria-label="Character classes">
                <label className="bgGenCheck">
                  <input type="checkbox" checked={rnd.lower} onChange={(e) => setRnd({ ...rnd, lower: e.target.checked })} />
                  <span>Lowercase</span>
                </label>
                <label className="bgGenCheck">
                  <input type="checkbox" checked={rnd.upper} onChange={(e) => setRnd({ ...rnd, upper: e.target.checked })} />
                  <span>Uppercase</span>
                </label>
                <label className="bgGenCheck">
                  <input type="checkbox" checked={rnd.digits} onChange={(e) => setRnd({ ...rnd, digits: e.target.checked })} />
                  <span>Digits</span>
                </label>
                <label className="bgGenCheck">
                  <input type="checkbox" checked={rnd.symbols} onChange={(e) => setRnd({ ...rnd, symbols: e.target.checked })} />
                  <span>Symbols</span>
                </label>
              </div>
            </div>
          )}

          {preset === 'pin' && (
            <div className="bgGenTunables">
              <label className="bgGenTunable">
                <span className="field-label">Length: {pin.length}</span>
                <input
                  type="range"
                  min={4}
                  max={12}
                  value={pin.length}
                  onChange={(e) => setPin({ length: Number(e.target.value) })}
                />
              </label>
            </div>
          )}

          <div className="bgGenPreview">
            <code className="bgGenValue" aria-label="Generated secret">
              {noCharset ? 'Choose at least one character class' : material}
            </code>
            <div className="bgGenPreviewActions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={regenerate} disabled={noCharset}>
                <Icon icon={RefreshCw} size="xs" /> Regenerate
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={copy} disabled={noCharset || !material}>
                {copied ? <><Icon icon={Check} size="xs" /> Copied</> : <><Icon icon={Copy} size="xs" /> Copy</>}
              </button>
            </div>
          </div>

          <div className="bgGenMeter">
            <div className="bgGenMeterHead">
              <span className={`bgGenStrength bgGenStrength-${strength.tone}`}>{strength.label}</span>
              <span className="bgGenBits">{Math.round(bits)} bits of entropy</span>
            </div>
            <div className="bgGenMeterTrack" aria-hidden="true">
              <div className={`bgGenMeterFill bgGenMeterFill-${strength.tone}`} style={{ width: `${strength.pct}%` }} />
            </div>
          </div>

          <span aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
            {copied ? 'Copied to clipboard' : ''}
          </span>

          <p className="bgShieldNote" style={{ marginTop: 0 }}>
            Generated in your browser from a bundled word list and your operating system&rsquo;s
            secure randomness. It is encrypted before it leaves your device.
          </p>
        </>
      )}

      {mode === 'provide' && (
        <div>
          <label className="field-label" htmlFor="ns-material">Secret material</label>
          <textarea
            id="ns-material"
            className="input"
            value={showProvide ? material : material.replace(/./g, '•')}
            onChange={(e) => {
              // When masked, edits would corrupt; only accept edits while revealed.
              if (showProvide) onMaterialChange(e.target.value);
            }}
            readOnly={!showProvide}
            placeholder="The credential to protect"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setShowProvide((v) => !v)}
            style={{ marginTop: '0.4rem' }}
            aria-pressed={showProvide}
          >
            {showProvide ? 'Hide' : 'Reveal'}
          </button>
          <p className="bgShieldNote" style={{ marginTop: '0.6rem' }}>
            Entered in your browser. It is encrypted before it leaves your device.
          </p>
        </div>
      )}
    </div>
  );
}

export default SecretGenerator;
